import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';

import AddUserModal from './AddUserModal';
import StaffTable from '../../../components/Staff/StaffTable';
import { getStaff, createStaff, resetPassword, updateStaff, getProfile } from '../../../services/UserService';
import { getRolesAndPermissions } from '../../../services/RoleService';
import { getLocations } from '../../../services/LocationService';
import { getBusinessUnits } from '../../../services/BusinessUnitService';
import PageHeader from '../../../components/PageHeader/PageHeader';
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { permissionOptions } from '../../../utils/Enums';

const StaffPage = () => {
  const { t } = useTranslation();
  const userPermissions = JSON.parse(localStorage.getItem('permissions')) || [];
  const hasPermission = (permissionLabel) => {
    const permissionId = permissionOptions.find((permission) => permission.label === permissionLabel)?.id;
    return permissionId ? userPermissions.includes(permissionId) : false;
  };
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [locations, setLocations] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteSessionPasswordModalOpen, setDeleteSessionPasswordModalOpen] = useState(false);
  const [deleteSessionStatusModalOpen, setDeleteSessionStatusModalOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [currentEmail, setCurrentEmail] = useState('');
  const [editingUserData, setEditingUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isBusinessUnitScoped, setIsBusinessUnitScoped] = useState(false);

  const loadPageData = async () => {
    try {
      setLoading(true);
      const profileData = await getProfile();
      const businessUnitScoped = profileData?.scopes?.length > 0 &&
        profileData.scopes.every((scope) => scope.scopeType === 'BUSINESS_UNIT');
      setIsBusinessUnitScoped(businessUnitScoped);

      let resolvedRoles = [];
      if (
        hasPermission('VIEW_ROLES_PERMISSIONS') ||
        hasPermission('VIEW_MANAGE_STAFF') ||
        hasPermission('CREATE_STAFF') ||
        hasPermission('UPDATE_STAFF')
      ) {
        const rolesData = await getRolesAndPermissions();
        resolvedRoles = rolesData.roles || [];
        if (businessUnitScoped) {
          resolvedRoles = resolvedRoles.filter((role) => role.name === 'Ventas' || role.name === 'Franquiciante');
        }
        setRoles(resolvedRoles);
      }

      const usersData = await getStaff();
      setUsers(usersData);

      if (hasPermission('VIEW_LOCATION')) {
        const locationsData = await getLocations();
        setLocations(locationsData || []);
      }

      if (hasPermission('VIEW_BUSINESS_UNITS') || hasPermission('VIEW_MANAGE_LOCATIONS')) {
        const businessUnitsData = await getBusinessUnits();
        setBusinessUnits(businessUnitsData || []);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPageData();
  }, []);

  const handleAddUser = async (newUser) => {
    // Sin try/catch: AddUserModal necesita ver el error para quedar abierto y mostrarlo
    const createdUser = await createStaff(newUser);
    setUsers([...users, createdUser]);
  };

  const handleResetPassword = async (email, newPassword, deleteSession) => {
    try {
      await resetPassword(email, newPassword, deleteSession);
    } catch (error) {
      console.error('Error resetting password:', error);
    }
  };

  const handleEditUser = async (userData) => {
    try {
      await updateStaff(userData);
      await loadPageData();
    } catch (error) {
      console.error('Error updating user:', error);
    }
  };

  const handleOpenSessionPasswordModal = (email, password) => {
    setCurrentEmail(email);
    setCurrentPassword(password);
    setDeleteSessionPasswordModalOpen(true);
  };

  const handleSessionPasswordModalClose = () => {
    setDeleteSessionPasswordModalOpen(false);
  };

  const handleSessionPasswordModalConfirm = (deleteSession) => {
    handleResetPassword(currentEmail, currentPassword, deleteSession);
    handleSessionPasswordModalClose();
  };

  const handleOpenSessionStatusModal = (email, data) => {
    setCurrentEmail(email);
    setEditingUserData({
      ...data,
      email,
      roleId: data.roleId ?? data.role?.id ?? '',
      dateOfAdmission: data.dateOfAdmission ? dayjs(data.dateOfAdmission) : dayjs(),
      birthdate: data.birthdate ? dayjs(data.birthdate) : (data.birthDate ? dayjs(data.birthDate) : dayjs()),
      deleteSession: false
    });
    setDeleteSessionStatusModalOpen(true);
  };

  const handleSessionStatusModalClose = () => {
    setDeleteSessionStatusModalOpen(false);
  };

  const handleSessionStatusModalConfirm = (deleteSession) => {
    handleEditUser({
      ...editingUserData,
      email: currentEmail,
      deleteSession
    });
    handleSessionStatusModalClose();
  };

  return (
    <Box p={3}>
      <PageHeader title={t('staff')} subtitle={t('manage_users_and_roles')} />
      <StaffTable 
        users={users} 
        loading={loading}
        onRefresh={loadPageData}
        onAddUser={() => setIsModalOpen(true)} 
        onResetPassword={(email, password) => {
          handleOpenSessionPasswordModal(email, password);
        }}
        onEditUser={(email, data) => {
          handleOpenSessionStatusModal(email, data);
        }}
        roles={roles}
        locations={locations}
        businessUnits={businessUnits}
        isBusinessUnitScoped={isBusinessUnitScoped}
      />
      <AddUserModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleAddUser}
        roles={roles}
        locations={locations}
        businessUnits={businessUnits}
        isBusinessUnitScoped={isBusinessUnitScoped}
      />
      <Dialog open={deleteSessionPasswordModalOpen} onClose={handleSessionPasswordModalClose}>
        <DialogTitle>{t('confirm_logout')}</DialogTitle>
        <DialogContent>
          <Typography>{t('confirm_logout_message')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSessionPasswordModalClose}>{t('cancel')}</Button>
          <Button onClick={() => handleSessionPasswordModalConfirm(false)}>{t('no')}</Button>
          <Button onClick={() => handleSessionPasswordModalConfirm(true)} variant="contained" color="primary">{t('yes')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteSessionStatusModalOpen} onClose={handleSessionStatusModalClose}>
        <DialogTitle>{t('confirm_logout')}</DialogTitle>
        <DialogContent>
          <Typography>{t('confirm_logout_message')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSessionStatusModalClose}>{t('cancel')}</Button>
          <Button onClick={() => handleSessionStatusModalConfirm(false)}>{t('no')}</Button>
          <Button onClick={() => handleSessionStatusModalConfirm(true)} variant="contained" color="primary">{t('yes')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StaffPage;
